import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import axios from "axios";
import {
  FaArrowLeft,
  FaCheck,
  FaChevronLeft,
  FaChevronRight,
  FaFutbol,
  FaSearch,
  FaSpinner,
  FaTicketAlt,
  FaTimes,
  FaTrash,
  FaPlus,
} from "react-icons/fa";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ACCESS_KEY = process.env.REACT_APP_ACCESKEY;

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");

  return token
    ? {
        Authorization: `Bearer ${token}`,
      }
    : {};
};

// filter useless markets

const categories = {
  "Goals Over/Under": [
    "Over 1.5",
    "Over 2.5",
    "Over 3.5",
    "Under 2.5",
    "Under 3.5",
    "Under 4.5",
  ],
  "Total - Home": [
    "Over 0.5",
    "Over 1.5", 
    "Over 2.5",
    "Under 0.5",
    "Under 1.5",
    "Under 2.5",
  ],
  "Total - Away": [
    "Over 0.5",
    "Over 1.5",
    "Over 2.5",
    "Under 0.5",
    "Under 1.5",
    "Under 2.5",
  ],
};

/*
|--------------------------------------------------------------------------
| Allowed ticket markets
|--------------------------------------------------------------------------
*/

const ALLOWED_MARKET_IDS = [
1, // Match Winner
5, // Goals Over/Under
8, // Both Teams Score
12, // Double Chance
16, // Total - Home
17, // Total - Away
];

function filterMarketValues(market) {
  const goalLineMarketIds = [5, 16, 17];

  // Keep all selections for other markets.
  if (!goalLineMarketIds.includes(Number(market.id))) {
    return market.values || [];
  }

  const allowedValues = categories[market.name];

  if (!allowedValues) {
    return market.values || [];
  }

  return (market.values || [])
    .filter((value) => allowedValues.includes(value.value))
    .sort((a, b) => {
      const lineA = Number(a.value.match(/\d+(?:\.\d+)?/)?.[0]);
      const lineB = Number(b.value.match(/\d+(?:\.\d+)?/)?.[0]);

      return lineA - lineB;
    });
}

const NewTicket = () => {
  const navigate = useNavigate();
  const location = useLocation();

  /*
  |--------------------------------------------------------------------------
  | Project
  |--------------------------------------------------------------------------
  */

  const initialProject = location.state?.project || null;

  const [project, setProject] = useState(initialProject);

  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);

  // Add Match modal
  const [isMatchModalOpen, setIsMatchModalOpen] = useState(false);
  const [modalStep, setModalStep] = useState("matches");

  // Match date filters
  const [matchFilter, setMatchFilter] = useState("today");
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");

  const [selectedDate, setSelectedDate] = useState(() => {
    const savedDate = sessionStorage.getItem("matchesSelectedDate");
    if (savedDate) {
      const parsed = new Date(savedDate);
      if (!isNaN(parsed.getTime())) {
        parsed.setHours(0, 0, 0, 0);
        return parsed;
      }
    }
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });

  // Draft selection: committed only when Confirm is clicked
  const [draftBet, setDraftBet] = useState(null);

  /*
  |--------------------------------------------------------------------------
  | Project steps
  |--------------------------------------------------------------------------
  */

  const [steps, setSteps] = useState([]);
  const [loadingSteps, setLoadingSteps] = useState(false);
  const [selectedStep, setSelectedStep] = useState(null);

  /*
  |--------------------------------------------------------------------------
  | Matches
  |--------------------------------------------------------------------------
  */

  const [matches, setMatches] = useState([]);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [matchSearch, setMatchSearch] = useState("");

  const [selectedMatch, setSelectedMatch] = useState(null);

  /*
  |--------------------------------------------------------------------------
  | MULTIPLE TICKET SELECTIONS
  |--------------------------------------------------------------------------
  */

  const [selectedBets, setSelectedBets] = useState([]);

  /*
  |--------------------------------------------------------------------------
  | Odds
  |--------------------------------------------------------------------------
  */

  const [oddsData, setOddsData] = useState(null);
  const [loadingOdds, setLoadingOdds] = useState(false);
  const [oddsError, setOddsError] = useState(null);

  /*
  |--------------------------------------------------------------------------
  | Ticket
  |--------------------------------------------------------------------------
  */

  const [stake, setStake] = useState("");
  const [bookmaker, setBookmaker] = useState("Bookmaker");
  const [note, setNote] = useState("");

  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  /*
  |--------------------------------------------------------------------------
  | Load projects
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    if (project) return;

    const fetchProjects = async () => {
      setLoadingProjects(true);

      try {
        const response = await axios.get(
          `${BACKEND_URL}/projects`,
          {
            headers: {
              ...getAuthHeaders(),
              "Content-Type": "application/json",
            },
          },
        );

        const data = response.data;

        setProjects(
          data.projects ||
            data.data ||
            [],
        );
      } catch (err) {
        console.error("Failed to load projects:", err);

        setError(
          err.response?.data?.error ||
            "Failed to load projects.",
        );
      } finally {
        setLoadingProjects(false);
      }
    };

    fetchProjects();
  }, [project]);

  // update date
  useEffect(() => {
    sessionStorage.setItem("matchesSelectedDate", selectedDate.toISOString());
  }, [selectedDate]);

  const prevDay = () => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() - 1);
    setSelectedDate(d);
  };

  const nextDay = () => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + 1);
    setSelectedDate(d);
  };

  // update stake
  useEffect(() => {
    if (!project) {
      setStake("");
      return;
    }

    if (project.type === "advanced") {
      const step = Number(
        project.current_step ?? 0,
      );

      const projectStake =
        step === 1
          ? project.initial_stake
          : project.current_stake;

      setStake(String(projectStake ?? ""));
    }
  }, [
    project?.id,
    project?.type,
    project?.current_step,
    project?.initial_stake,
    project?.current_stake,
  ]);
  

  /*
  |--------------------------------------------------------------------------
  | Load project steps
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    if (!project?.id) {
      setSteps([]);
      return;
    }

    const fetchSteps = async () => {
      setLoadingSteps(true);

      try {
        const response = await axios.get(
          `${BACKEND_URL}/projects/${project.id}/steps`,
          {
            headers: {
              ...getAuthHeaders(),
              "Content-Type": "application/json",
            },
          },
        );

        setSteps(
          response.data.steps ||
            response.data.project_steps ||
            [],
        );
      } catch (err) {
        console.error(
          "Failed to load project steps:",
          err,
        );

        setSteps([]);
      } finally {
        setLoadingSteps(false);
      }
    };

    fetchSteps();
  }, [project]);

  /*
  |--------------------------------------------------------------------------
  | Load matches
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    const fetchMatches = async () => {
      setLoadingMatches(true);

      try {
        const response = await axios.get(
          `${BACKEND_URL}/matches/getrelevant`,
          {
            headers: {
              ...getAuthHeaders(),
              "Content-Type": "application/json",
            },
          },
        );

        setMatches(
          response.data.matches || [],
        );
      } catch (err) {
        console.error(
          "Failed to load matches:",
          err,
        );

        setError(
          err.response?.data?.error ||
            "Failed to load matches.",
        );
      } finally {
        setLoadingMatches(false);
      }
    };

    fetchMatches();
  }, []);

  /*
  |--------------------------------------------------------------------------
  | Load odds whenever active match changes
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    if (!selectedMatch?.id) {
      setOddsData(null);
      setOddsError(null);
      return;
    }

    let mounted = true;

    const fetchOdds = async () => {
      setLoadingOdds(true);
      setOddsError(null);
      setOddsData(null);

      try {
        const response = await fetch(
          `${BACKEND_URL}/odds/get?fixtureId=${selectedMatch.id}&bookmakerId=8`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              key: ACCESS_KEY,
            },
          },
        );

        if (!response.ok) {
          throw new Error(
            `HTTP ${response.status}`,
          );
        }

        const json = await response.json();

        if (mounted) {
          setOddsData(json);
        }
      } catch (err) {
        console.error(
          "Failed to load odds:",
          err,
        );

        if (mounted) {
          setOddsError(
            "Failed to load odds for this match.",
          );
        }
      } finally {
        if (mounted) {
          setLoadingOdds(false);
        }
      }
    };

    fetchOdds();

    return () => {
      mounted = false;
    };
  }, [selectedMatch]);

  /*
  |--------------------------------------------------------------------------
  | Extract bookmaker
  |--------------------------------------------------------------------------
  */

  const bookmakerData = useMemo(() => {
    return (
      oddsData?.response?.[0]?.bookmakers?.[0] ||
      null
    );
  }, [oddsData]);

  const markets = useMemo(() => {
    const bookmakerMarkets = bookmakerData?.bets || [];

    return bookmakerMarkets.filter((market) =>
        ALLOWED_MARKET_IDS.includes(
        Number(market.id),
        ),
    );
  }, [bookmakerData]);

  /*
  |--------------------------------------------------------------------------
  | Filter matches
  |--------------------------------------------------------------------------
  */

  const filteredMatches = useMemo(() => {
    const query = matchSearch.trim().toLowerCase();

    return matches.filter((match) => {
      // Existing team and league search
      const searchableText = [
        match.homeTeam,
        match.awayTeam,
        match.league,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      if (query && !searchableText.includes(query)) {
        return false;
      }

      if (!match.date) {
        return false;
      }

      const matchDate = new Date(match.date);

      if (Number.isNaN(matchDate.getTime())) {
        return false;
      }

      return (
        matchDate.getFullYear() === selectedDate.getFullYear() &&
        matchDate.getMonth() === selectedDate.getMonth() &&
        matchDate.getDate() === selectedDate.getDate()
      );

    });
  }, [
    matches,
    matchSearch,
    selectedDate,
  ]);

  /*
  |--------------------------------------------------------------------------
  | Ticket calculations
  |--------------------------------------------------------------------------
  */

  const parsedStake = Number(stake);

  const totalOdd = useMemo(() => {
    if (selectedBets.length === 0) return 0;

    return selectedBets.reduce((total, bet) => {
      const odd = Number(bet.odd);

      if (!Number.isFinite(odd) || odd <= 0) {
        return 0;
      }

      return total * odd;
    }, 1);
  }, [selectedBets]);

  const potentialReturn =
    Number.isFinite(parsedStake) &&
    parsedStake > 0 &&
    totalOdd > 0
      ? parsedStake * totalOdd
      : 0;

  const potentialProfit =
    potentialReturn > 0
      ? potentialReturn - parsedStake
      : 0;

  
  /*
  |--------------------------------------------------------------------------
  | Advanced project rules
  |--------------------------------------------------------------------------
  */

  const isAdvancedProject =
  project?.type === "advanced";

  const currentStepNumber = Number(
    project?.current_step ?? 0,
  );

  const isFirstStep =
    currentStepNumber === 1;

  const requiredStake = isAdvancedProject
    ? Number(
        isFirstStep
          ? project.initial_stake
          : project.current_stake,
      )
    : null;

  const targetOdd = Number(
    project?.target_odd ?? 0,
  );

  const minAllowedOdd = targetOdd * 0.75;
  const maxAllowedOdd = targetOdd * 1.25;

  const isTotalOddInRange =
    !isAdvancedProject ||
    (
      targetOdd > 0 &&
      totalOdd >= minAllowedOdd &&
      totalOdd <= maxAllowedOdd
    );


  /*
  |--------------------------------------------------------------------------
  | Check if match already has a selection
  |--------------------------------------------------------------------------
  */

  const getBetForMatch = (matchId) => {
    return selectedBets.find(
      (bet) =>
        String(bet.match?.id) ===
        String(matchId),
    );
  };

  /*
  |--------------------------------------------------------------------------
  | Select match
  |--------------------------------------------------------------------------
  */

  const handleOpenMatchModal = () => {
    setMatchSearch("");
    setMatchFilter("today");
    setRangeStart("");
    setRangeEnd("");
    setDraftBet(null);
    setSelectedMatch(null);
    setModalStep("matches");
    setOddsError(null);
    setError(null);
    setIsMatchModalOpen(true);
  };

  const handleSelectMatch = (match) => {
    setSelectedMatch(match);
    setDraftBet(null);
    setOddsError(null);
    setError(null);
    setModalStep("markets");
  };

  const handleBackToMatches = () => {
    setSelectedMatch(null);
    setDraftBet(null);
    setOddsError(null);
    setModalStep("matches");
  };

  const handleCloseMatchModal = () => {
    setIsMatchModalOpen(false);
    setSelectedMatch(null);
    setDraftBet(null);
    setModalStep("matches");
  };

  const handleConfirmSelection = () => {
    if (!draftBet || !draftBet.match?.id) {
      setError("Please select a market and odd first.");
      return;
    }

    if (!Number.isFinite(Number(draftBet.odd)) ||
        Number(draftBet.odd) <= 0) {
      setError("Please select a valid odd.");
      return;
    }

    setSelectedBets((current) => {
      const index = current.findIndex(
        (bet) =>
          String(bet.match?.id) === String(draftBet.match.id)
      );

      if (index === -1) {
        return [...current, draftBet];
      }

      const updated = [...current];
      updated[index] = draftBet;

      return updated;
    });

    setError(null);
    handleCloseMatchModal();
  };

  /*
  |--------------------------------------------------------------------------
  | Select odd
  |--------------------------------------------------------------------------
  */

  const handleSelectOdd = (market, value) => {
    if (!selectedMatch?.id) return;

    if (!ALLOWED_MARKET_IDS.includes(Number(market.id))) {
      setError("This market is not available for ticket selection.");
      return;
    }

    setDraftBet({
      match: selectedMatch,
      marketId: market.id,
      marketName: market.name,
      value: value.value,
      odd: Number(value.odd),
    });

    setError(null);
  };

  // edit odd

  const handleEditOdd = (matchId, rawOdd) => {
    setSelectedBets((current) =>
      current.map((bet) => {
        if (String(bet.match?.id) !== String(matchId)) {
          return bet;
        }

        return {
          ...bet,
          odd: rawOdd,
        };
      })
    );
  };

  /*
  |--------------------------------------------------------------------------
  | Remove selection
  |--------------------------------------------------------------------------
  */

  const handleRemoveBet = (matchId) => {
    setSelectedBets((current) =>
      current.filter(
        (bet) =>
          String(bet.match?.id) !==
          String(matchId),
      ),
    );
  };

  /*
  |--------------------------------------------------------------------------
  | Clear all selections
  |--------------------------------------------------------------------------
  */

  const handleClearBets = () => {
    setSelectedBets([]);
    setError(null);
  };

  /*
  |--------------------------------------------------------------------------
  | Create ticket
  |--------------------------------------------------------------------------
  */

  const handleCreateTicket = async () => {
    setError(null);

    /*
     * Validate project
     */

    if (!project?.id) {
      setError(
        "Please select a project.",
      );
      return;
    }

    /*
     * Validate selections
     */

    if (selectedBets.length === 0) {
      setError(
        "Please select at least one match and odd.",
      );
      return;
    }

    /*
     * Validate stake
     */

    if (
      !Number.isFinite(parsedStake) ||
      parsedStake <= 0
    ) {
      setError(
        "Please enter a valid stake.",
      );
      return;
    }

    /*
     * Validate total odd
     */

    if (
      !Number.isFinite(totalOdd) ||
      totalOdd <= 0
    ) {
      setError(
        "Invalid total odd.",
      );
      return;
    }

    /*
     * Validate individual selections
     */

    for (const bet of selectedBets) {
      if (
        !bet.match?.id ||
        !Number.isFinite(
          Number(bet.odd),
        ) ||
        Number(bet.odd) <= 0 ||
        !bet.marketName ||
        !bet.value
      ) {
        setError(
          "One of your selections is invalid. Please review your ticket.",
        );
        return;
      }
    }

    if (isAdvancedProject) {
      const expectedStake =
        Number(
          currentStepNumber === 1
            ? project.initial_stake
            : project.current_stake,
        );

      if (
        !Number.isFinite(expectedStake) ||
        expectedStake <= 0 ||
        Math.abs(parsedStake - expectedStake) > 0.001
      ) {
        setError(
          "The stake does not match the current project step.",
        );
        return;
      }
    }

    /*
    |--------------------------------------------------------------------------
    | Backend payload
    |--------------------------------------------------------------------------
    */

    const payload = {
      project_id: Number(
        project.id,
      ),

      step_id: selectedStep?.id
        ? Number(selectedStep.id)
        : null,

      stake: parsedStake,

      total_odd: Number(
        totalOdd.toFixed(4),
      ),

      potential_return: Number(
        potentialReturn.toFixed(2),
      ),

      bookmaker:
        bookmakerData?.name ||
        bookmaker ||
        null,

      note:
        note.trim() || null,

      selections:
        selectedBets.map((bet) => ({
          match_id: String(
            bet.match.id,
          ),

          market: bet.marketName,

          selection: String(
            bet.value,
          ),

          line: extractLine(
            bet.value,
          ),

          odd: Number(bet.odd),
        })),
    };

    console.log(
      "Creating ticket with payload:",
      payload,
    );

    setCreating(true);

    try {
      const response =
        await axios.post(
          `${BACKEND_URL}/tickets`,
          payload,
          {
            headers: {
              ...getAuthHeaders(),
              "Content-Type":
                "application/json",
            },
          },
        );

      console.log(
        "Ticket created:",
        response.data,
      );

      setSuccess(true);

      const createdTicket =
        response.data?.ticket;

      setTimeout(() => {
        if (createdTicket?.id) {
          navigate(
            `/tickets/${createdTicket.id}`,
            {
              replace: true,
            },
          );
        } else {
          navigate("/tickets", {
            replace: true,
          });
        }
      }, 700);
    } catch (err) {
      console.error(
        "Create ticket error:",
        err,
      );

      const backendError =
        err.response?.data?.error;

      const backendMessage =
        err.response?.data?.message;

      setError(
        backendError ||
          backendMessage ||
          "Could not create the ticket. Please try again.",
      );
    } finally {
      setCreating(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Render
  |--------------------------------------------------------------------------
  */

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 pb-10">
        <div className="max-w-6xl mx-auto p-4 md:p-6">

          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            {/* <button
              onClick={() =>
                navigate(-1)
              }
              className="w-10 h-10 rounded-lg bg-gray-800 border border-gray-700 flex items-center justify-center text-gray-300 hover:text-white hover:border-green-500/50 transition-colors"
            >
              <FaArrowLeft />
            </button> */}
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
            >
              <FaArrowLeft />
              Go back
            </button>

            {/* <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <FaTicketAlt className="text-green-400" />

                <span className="bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
                  New Ticket
                </span>
              </h1>

              <p className="text-gray-400 text-sm mt-1">
                Add one or more matches to your ticket.
              </p>
            </div> */}
          </div>

          {/* Error */}
          {error && (
            <div className="mb-5 p-4 rounded-xl bg-red-950/40 border border-red-500/30 text-red-300 flex items-start gap-3">
              <FaTimes className="mt-0.5 shrink-0" />

              <div className="flex-1">
                <p className="font-medium">
                  {error}
                </p>
              </div>

              <button
                onClick={() =>
                  setError(null)
                }
                className="text-red-400 hover:text-white"
              >
                <FaTimes />
              </button>
            </div>
          )}

          {/* Success */}
          {success && (
            <div className="mb-5 p-4 rounded-xl bg-green-950/40 border border-green-500/30 text-green-300 flex items-center gap-3">
              <FaCheck />

              <div>
                <p className="font-medium">
                  Ticket created successfully.
                </p>

                <p className="text-sm text-green-400/70">
                  Opening ticket...
                </p>
              </div>
            </div>
          )}

          {/* Project */}
          <section className="bg-gray-800/60 border border-green-500/20 rounded-xl p-5 mb-5">

            {project ? (
              <div className="p-4 rounded-xl bg-green-900/20 border border-green-500/30">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-green-400 uppercase">
                      Selected Project
                    </p>

                    <p className="text-white font-semibold mt-1">
                      {project.name ||
                        `Project #${project.id}`}
                    </p>
                  </div>

                  <FaCheck className="text-green-400" />
                </div>
              </div>
            ) : loadingProjects ? (
              <div className="flex items-center gap-2 text-gray-400">
                <FaSpinner className="animate-spin" />
                Loading projects...
              </div>
            ) : projects.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {projects.map(
                  (item) => (
                    <button
                      key={item.id}
                      onClick={() => {
                        setProject(item);
                        setSelectedStep(
                          null,
                        );
                      }}
                      className="text-left p-4 rounded-xl bg-gray-700/50 border border-gray-600 hover:border-green-500/50 hover:bg-gray-700 transition-all"
                    >
                      <p className="text-white font-medium">
                        {item.name ||
                          `Project #${item.id}`}
                      </p>

                      {item.type && (
                        <p className="text-xs text-gray-400 mt-1">
                          {item.type}
                        </p>
                      )}
                    </button>
                  ),
                )}
              </div>
            ) : (
              <p className="text-gray-400">
                No projects available.
              </p>
            )}
          </section>

          {/* Step */}
          {project &&
            steps.length > 0 && (
              <section className="bg-gray-800/60 border border-green-500/20 rounded-xl p-5 mb-5">
                <h2 className="text-lg font-semibold text-white">
                  2. Project Step
                </h2>

                <p className="text-sm text-gray-500 mb-4">
                  Select the step this ticket belongs to.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {steps.map(
                    (step) => {
                      const isSelected =
                        selectedStep?.id ===
                        step.id;

                      return (
                        <button
                          key={step.id}
                          onClick={() =>
                            setSelectedStep(
                              step,
                            )
                          }
                          className={`text-left p-4 rounded-xl border transition-all ${
                            isSelected
                              ? "bg-green-900/30 border-green-500"
                              : "bg-gray-700/50 border-gray-600 hover:border-green-500/50"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-xs text-gray-400">
                                Step
                              </p>

                              <p className="text-white font-semibold">
                                {step.step_number ??
                                  step.id}
                              </p>
                            </div>

                            {isSelected && (
                              <FaCheck className="text-green-400" />
                            )}
                          </div>

                          {step.stake !==
                            undefined && (
                            <p className="text-xs text-gray-400 mt-2">
                              Stake:{" "}
                              {Number(
                                step.stake,
                              ).toFixed(
                                2,
                              )}
                            </p>
                          )}

                          {step.target_odd !==
                            undefined && (
                            <p className="text-xs text-gray-400 mt-1">
                              Target odd:{" "}
                              {Number(
                                step.target_odd,
                              ).toFixed(
                                2,
                              )}
                            </p>
                          )}
                        </button>
                      );
                    },
                  )}
                </div>
              </section>
            )}

          {/* Selected Bets */}
          {selectedBets.length > 0 && (
          <section className="bg-gray-800/60 border border-green-500/30 rounded-xl p-5 mb-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <FaCheck className="text-green-400" />
                  Selected Bets
                </h2>

                <p className="text-sm text-gray-500 mt-1">
                  {selectedBets.length}{" "}
                  {selectedBets.length === 1
                    ? "match"
                    : "matches"}{" "}
                  selected
                </p>
              </div>

              <div className="flex items-center gap-2">
                {selectedBets.length > 0 && (
                  <button
                    onClick={handleClearBets}
                    className="px-3 py-2 text-xs text-gray-400
                      hover:text-red-400"
                  >
                    Clear all
                  </button>
                )}

                <button
                  onClick={handleOpenMatchModal}
                  className="px-4 py-2.5 rounded-xl bg-green-600
                    hover:bg-green-500 text-white font-semibold
                    text-sm flex items-center gap-2"
                >
                  <FaPlus />
                  Add Match
                </button>
              </div>
            </div>

            <div className="space-y-2">
              {selectedBets.map(
                (bet) => (
                  <div
                    key={String(
                      bet.match.id,
                    )}
                    className="p-3 rounded-xl bg-gray-900/60 border border-gray-700"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-green-900/40 flex items-center justify-center shrink-0">
                        <FaFutbol className="text-green-400" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">
                          {bet.match.homeTeam}
                          <span className="text-gray-500 mx-1">
                            vs
                          </span>
                          {bet.match.awayTeam}
                        </p>

                        <p className="text-xs text-gray-400 mt-1">
                          {bet.marketName}
                          {" • "}
                          <span className="text-white">
                            {bet.value}
                          </span>
                        </p>
                      </div>

                      <div className="flex items-center gap-3 shrink-0">
                        <div className="shrink-0 w-24">
                          <label
                            htmlFor={`odd-${bet.match.id}`}
                            className="block text-[10px] text-gray-500 mb-1"
                          >
                            Your odds
                          </label>

                          <input
                            id={`odd-${bet.match.id}`}
                            type="number"
                            min="1.01"
                            step="0.01"
                            inputMode="decimal"
                            value={bet.odd}
                            onChange={(e) =>
                              handleEditOdd(bet.match.id, e.target.value)
                            }
                            className="w-full px-2 py-2 rounded-lg bg-gray-800
                              border border-gray-600 text-green-400 font-bold
                              text-sm focus:outline-none focus:ring-2
                              focus:ring-green-500/50"
                          />
                        </div>

                        <button
                          onClick={() =>
                            handleRemoveBet(
                              bet.match
                                .id,
                            )
                          }
                          className="w-8 h-8 rounded-lg bg-gray-700 text-gray-400 hover:bg-red-900/50 hover:text-red-400 flex items-center justify-center transition-colors"
                          title="Remove selection"
                        >
                          <FaTrash size={12} />
                        </button>
                      </div>
                    </div>
                  </div>
                ),
              )}
            </div>
          </section>
          )}

          {/* Help when no selections */}
          {selectedBets.length === 0 && (
            <div className="bg-gray-800/40 border border-gray-700 rounded-xl p-8 text-center">
              <div className="w-14 h-14 rounded-2xl bg-green-900/30 border border-green-500/20 flex items-center justify-center mx-auto mb-4">
                <FaFutbol className="text-green-400 text-2xl" />
              </div>

              <h3 className="text-white text-lg font-semibold">
                Add your first match
              </h3>

              <p className="text-gray-400 text-sm mt-2 max-w-md mx-auto">
                Start building your ticket by choosing a match and selecting your preferred market and odds.
              </p>

              <button
                type="button"
                onClick={handleOpenMatchModal}
                className="mt-5 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-green-600 hover:bg-green-500 text-white font-semibold text-sm transition-colors"
              >
                <FaPlus />
                Add your first match
              </button>
            </div>
          )}

          {/* Ticket Details / Sticky Summary */}
          {(
            <section className="sticky bottom-4 z-30 bg-gray-900/95 backdrop-blur-xl border border-green-500/30 rounded-xl p-4 md:p-5 shadow-2xl shadow-black/40">

              {/* Selected summary */}
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">

                {/* Left side */}
                <div className="flex-1 min-w-0">

                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div>
                      <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                        <FaTicketAlt className="text-green-400" />

                        Ticket
                        <span className="text-green-400">
                          ({selectedBets.length})
                        </span>
                      </h2>

                      <p className="text-xs text-gray-500 mt-1">
                        {selectedBets.length ===
                        1
                          ? "1 selection"
                          : `${selectedBets.length} selections`}
                        {" • "}
                        Total odd{" "}
                        <span className="text-green-400 font-semibold">
                          {totalOdd.toFixed(
                            2,
                          )}
                        </span>
                      </p>
                    </div>

                    <button
                      onClick={
                        handleClearBets
                      }
                      className="lg:hidden text-xs text-gray-400 hover:text-red-400"
                    >
                      Clear
                    </button>
                  </div>

                  {/* Compact selected bets */}
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {selectedBets.map(
                      (bet) => (
                        <div
                          key={String(
                            bet.match.id,
                          )}
                          className="flex items-center gap-2 shrink-0 px-3 py-2 rounded-lg bg-gray-800 border border-gray-700"
                        >
                          <div className="min-w-0 max-w-[220px]">
                            <p className="text-xs text-white truncate">
                              {
                                bet.match
                                  .homeTeam
                              }
                              {" vs "}
                              {
                                bet.match
                                  .awayTeam
                              }
                            </p>

                            <p className="text-[11px] text-gray-500 truncate">
                              {
                                bet.value
                              }
                            </p>
                          </div>

                          <span className="text-xs font-bold text-green-400">
                            {Number(
                              bet.odd,
                            ).toFixed(
                              2,
                            )}
                          </span>

                          <button
                            onClick={() =>
                              handleRemoveBet(
                                bet.match
                                  .id,
                              )
                            }
                            className="text-gray-500 hover:text-red-400"
                          >
                            <FaTimes size={11} />
                          </button>
                        </div>
                      ),
                    )}
                  </div>
                </div>

                {/* Stake */}
                <div className="w-full lg:w-36">
                  <label className="block text-xs text-gray-400 mb-1.5">
                    Stake
                  </label>

                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={stake}
                    readOnly={isAdvancedProject}
                    onChange={(e) =>
                      setStake(e.target.value)
                    }
                    placeholder="Stake"
                    className={`w-full px-3 py-2.5 border rounded-lg
                      text-white placeholder-gray-500
                      focus:outline-none focus:ring-2
                      focus:ring-green-500/50 ${
                        isAdvancedProject
                          ? "bg-gray-900 border-green-500/30 cursor-not-allowed"
                          : "bg-gray-800 border-gray-700"
                      }`}
                  />
                </div>

                {/* Summary */}
                <div className="grid grid-cols-3 gap-2 w-full lg:w-auto">

                  <SummaryCard
                    label="Odd"
                    value={totalOdd.toFixed(
                      2,
                    )}
                    color="text-green-400"
                  />

                  <SummaryCard
                    label="Return"
                    value={potentialReturn.toFixed(
                      2,
                    )}
                    color="text-white"
                  />

                  <SummaryCard
                    label="Profit"
                    value={potentialProfit.toFixed(
                      2,
                    )}
                    color="text-emerald-400"
                  />

                </div>

                {/* Create button */}
                <button
                  onClick={
                    handleCreateTicket
                  }
                  disabled={
                    creating ||
                    success ||
                    !project?.id ||
                    selectedBets.length === 0 ||
                    !Number.isFinite(parsedStake) ||
                    parsedStake <= 0 ||
                    (isAdvancedProject && !isTotalOddInRange)
                  }
                  className="w-full lg:w-auto lg:min-w-[190px] py-3 px-5 rounded-xl bg-gradient-to-r from-green-600 to-emerald-600 text-white font-semibold hover:from-green-500 hover:to-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 shadow-lg shadow-green-900/20"
                >
                  {creating ? (
                    <>
                      <FaSpinner className="animate-spin" />
                      Creating...
                    </>
                  ) : success ? (
                    <>
                      <FaCheck />
                      Created
                    </>
                  ) : (
                    <>
                      <FaTicketAlt />
                      Create Ticket
                    </>
                  )}
                </button>
              </div>

              {/* Additional details */}
              <div className="mt-4 pt-4 border-t border-gray-800 grid grid-cols-1 md:grid-cols-2 gap-3">
                {isAdvancedProject && (
                  <div className="mt-2 text-xs">
                    <p className="text-gray-400">
                      Target odd:{" "}
                      <span className="text-white font-semibold">
                        {targetOdd.toFixed(2)}
                      </span>
                    </p>

                    <p
                      className={
                        isTotalOddInRange
                          ? "text-green-400 mt-1"
                          : "text-amber-400 mt-1"
                      }
                    >
                      Allowed range:{" "}
                      {minAllowedOdd.toFixed(2)}
                      {" – "}
                      {maxAllowedOdd.toFixed(2)}
                    </p>

                    <p
                      className={
                        isTotalOddInRange
                          ? "text-green-400 mt-1"
                          : "text-amber-400 mt-1"
                      }
                    >
                      {isTotalOddInRange
                        ? "Total odd is within the allowed range."
                        : totalOdd < minAllowedOdd
                          ? "Add more selections to increase the total odd."
                          : "Remove or change selections to reduce the total odd."}
                    </p>
                  </div>
                )}

                {/* Note */}
                <div>
                  <label className="block text-xs text-gray-400 mb-1.5">
                    Note
                  </label>

                  <input
                    type="text"
                    value={note}
                    onChange={(e) =>
                      setNote(
                        e.target.value,
                      )
                    }
                    placeholder="Optional note..."
                    className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500/50"
                  />
                </div>

              </div>
            </section>
          )}

        </div>

      {/* Add Match modal */}
      {isMatchModalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm
            flex items-center justify-center p-3 sm:p-6"
          // onMouseDown={(e) => {
          //   if (e.target === e.currentTarget) {
          //     handleCloseMatchModal();
          //   }
          // }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="match-modal-title"
            className="w-full max-w-4xl max-h-[90vh] overflow-hidden
              flex flex-col bg-gray-900 border border-gray-700
              rounded-2xl shadow-2xl"
          >
            {/* Modal header */}
            <div className="p-5 border-b border-gray-700 flex
              items-center justify-between gap-3">
              <div>
                <h2 id="match-modal-title"
                  className="text-xl font-bold text-white">
                  {modalStep === "matches"
                    ? "Add a match"
                    : "Choose your selection"}
                </h2>
                <p className="text-sm text-gray-400 mt-1">
                  {modalStep === "matches"
                    ? "Find the match you want to add."
                    : `${selectedMatch?.homeTeam} vs ${selectedMatch?.awayTeam}`}
                </p>
              </div>

              <button
                onClick={handleCloseMatchModal}
                className="w-10 h-10 rounded-lg bg-gray-800
                  text-gray-400 hover:text-white flex
                  items-center justify-center"
                aria-label="Close modal"
              >
                <FaTimes />
              </button>
            </div>

            {/* Match selection */}
            {modalStep === "matches" && (
              <div className="p-4 sm:p-5 overflow-y-auto">

                {/* Search */}
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 gap-4">
                  <div className="relative w-full md:w-64">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaSearch className="text-gray-400" />
                    </div>
                    <input
                      type="search"
                      value={matchSearch}
                      onChange={(e) => setMatchSearch(e.target.value)}
                      placeholder="Search team or league..."
                      className="w-full pl-10 pr-4 py-2.5 bg-gray-800/80 border border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>

                  <div className="flex items-right gap-2 bg-gray-800/80 border border-gray-700 px-3 py-1.5 rounded-xl">
                    <button
                      onClick={prevDay}
                      className="p-2 text-green-400 hover:text-green-300 hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <FaChevronLeft />
                    </button>
                    <div className="text-sm font-medium text-white px-3 py-1">
                      {selectedDate.toLocaleDateString(undefined, {
                        weekday: "short",
                        day: "numeric",
                        month: "short",
                      })}
                    </div>
                    <button
                      onClick={nextDay}
                      className="p-2 text-green-400 hover:text-green-300 hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <FaChevronRight />
                    </button>
                  </div>
                </div>

                {/* Matches */}
                {loadingMatches ? (
                  <div className="py-12 flex justify-center
                    items-center text-gray-400 gap-2">
                    <FaSpinner className="animate-spin" />
                    Loading matches...
                  </div>
                ) : filteredMatches.length === 0 ? (
                  <div className="py-12 text-center
                    text-gray-400">
                    <FaFutbol className="mx-auto mb-3 text-2xl
                      text-gray-600" />
                    <p>No matches found.</p>
                    <p className="text-sm mt-1">
                      Try another date filter or search.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500 mb-2">
                      {filteredMatches.length} matches found
                    </p>

                    {filteredMatches.map((match) => {
                      const existingBet = getBetForMatch(match.id);

                      return (
                        <button
                          key={match.id}
                          onClick={() => handleSelectMatch(match)}
                          className="w-full text-left p-4 rounded-xl
                            border border-gray-700 bg-gray-800/60
                            hover:border-green-500/60
                            hover:bg-gray-800 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <FaFutbol className="text-green-400
                              shrink-0" />

                            <div className="flex-1 min-w-0">
                              <p className="text-white font-medium">
                                {match.homeTeam} vs {match.awayTeam}
                              </p>
                              <p className="text-xs text-gray-400 mt-1">
                                {match.league || "Football"}
                                {match.date
                                  ? ` · ${formatDateTime(match.date)}`
                                  : ""}
                              </p>
                              {existingBet && (
                                <p className="text-xs text-green-400 mt-1">
                                  Already added · {existingBet.value}
                                </p>
                              )}
                            </div>

                            <FaChevronRight className="text-gray-500" />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Market and odds selection */}
            {modalStep === "markets" && (
              <>
                <div className="p-4 sm:p-5 overflow-y-auto flex-1">
                  <div className="p-3 mb-4 rounded-xl
                    bg-gray-800 border border-gray-700">
                    <p className="text-white font-medium">
                      {selectedMatch?.homeTeam} vs {selectedMatch?.awayTeam}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {selectedMatch?.league}
                      {selectedMatch?.date
                        ? ` · ${formatDateTime(selectedMatch.date)}`
                        : ""}
                    </p>
                  </div>

                  {loadingOdds ? (
                    <div className="py-12 flex justify-center
                      items-center gap-2 text-gray-400">
                      <FaSpinner className="animate-spin" />
                      Loading markets and odds...
                    </div>
                  ) : oddsError ? (
                    <div className="p-4 rounded-xl bg-red-950/30
                      border border-red-500/30 text-red-300">
                      {oddsError}
                      <button
                        onClick={handleBackToMatches}
                        className="block mt-3 text-sm underline"
                      >
                        Back to matches
                      </button>
                    </div>
                  ) : markets.length === 0 ? (
                    <div className="p-8 text-center rounded-xl
                      bg-gray-800/50 border border-gray-700">
                      <p className="text-white font-medium">
                        No odds available
                      </p>
                      <p className="text-sm text-gray-400 mt-2">
                        Choose another match to continue.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {markets.map((market) => (
                        <div
                          key={market.id}
                          className="rounded-xl bg-gray-800/50
                            border border-gray-700 overflow-hidden"
                        >
                          <h3 className="px-4 py-3 font-semibold
                            text-gray-200 bg-gray-800
                            border-b border-gray-700">
                            {market.name}
                          </h3>

                          <div className="p-3 grid grid-cols-1
                            sm:grid-cols-2 gap-2">
                            {filterMarketValues(market).map(
                              (value, index) => {
                                const isSelected =
                                  draftBet?.marketId != null &&
                                  String(draftBet.marketId) ===
                                    String(market.id) &&
                                  String(draftBet.value) ===
                                    String(value.value) &&
                                  Number(draftBet.odd) ===
                                    Number(value.odd);

                                return (
                                  <button
                                    key={`${market.id}-${index}`}
                                    onClick={() =>
                                      handleSelectOdd(market, value)
                                    }
                                    className={`p-3 rounded-lg border
                                      text-left transition-colors ${
                                        isSelected
                                          ? "bg-green-900/40 border-green-500"
                                          : "bg-gray-800 border-gray-700 hover:border-green-500/50"
                                      }`}
                                  >
                                    <div className="flex items-center
                                      justify-between gap-2">
                                      <span className="text-sm
                                        text-gray-200">
                                        {value.value}
                                      </span>
                                      <span className="font-bold
                                        text-green-400">
                                        {Number(value.odd).toFixed(2)}
                                      </span>
                                    </div>

                                    {isSelected && (
                                      <p className="text-xs text-green-400
                                        mt-2 flex items-center gap-1">
                                        <FaCheck /> Selected
                                      </p>
                                    )}
                                  </button>
                                );
                              }
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {draftBet && (
                    <div className="mt-4 p-3 rounded-xl
                      bg-green-900/20 border border-green-500/30">
                      <p className="text-xs text-green-400">
                        Your selection
                      </p>
                      <p className="text-white font-medium mt-1">
                        {draftBet.marketName}: {draftBet.value}
                      </p>
                      <p className="text-sm text-green-400 mt-1">
                        Odd: {Number(draftBet.odd).toFixed(2)}
                      </p>
                    </div>
                  )}
                </div>

                {/* Modal actions */}
                <div className="p-4 border-t border-gray-700
                  flex items-center justify-between gap-3">
                  <button
                    onClick={handleBackToMatches}
                    className="px-4 py-3 rounded-xl bg-gray-800
                      border border-gray-700 text-gray-200
                      hover:bg-gray-700 flex items-center gap-2"
                  >
                    <FaArrowLeft /> Back
                  </button>

                  <button
                    onClick={handleConfirmSelection}
                    disabled={
                      !draftBet ||
                      !Number.isFinite(Number(draftBet.odd)) ||
                      Number(draftBet.odd) <= 0
                    }
                    className="px-5 py-3 rounded-xl bg-green-600
                      hover:bg-green-500 text-white font-semibold
                      disabled:opacity-40 disabled:cursor-not-allowed
                      flex items-center gap-2"
                  >
                    <FaCheck /> Confirm selection
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      </div>
    </Layout>
  );
};

/*
|--------------------------------------------------------------------------
| Helpers
|--------------------------------------------------------------------------
*/

function extractLine(value) {
  if (
    value === null ||
    value === undefined
  ) {
    return null;
  }

  const stringValue =
    String(value);

  const match =
    stringValue.match(
      /[-+]?\d+(?:\.\d+)?/,
    );

  return match ? match[0] : null;
}

function formatDateTime(value) {
  if (!value) return "";

  try {
    return new Date(
      value,
    ).toLocaleString(
      undefined,
      {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      },
    );
  } catch {
    return "";
  }
}

function SummaryCard({
  label,
  value,
  color,
}) {
  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 min-w-0">
      <p className="text-[10px] text-gray-500 uppercase truncate">
        {label}
      </p>

      <p
        className={`text-lg font-bold mt-0.5 ${color}`}
      >
        {value}
      </p>
    </div>
  );
}

export default NewTicket;